import localforage from 'localforage';

// Create a separate store for content cache (PDFs, videos, etc.)
const contentCacheStore = localforage.createInstance({
  name: 'Tinat Academy',
  storeName: 'content_cache',
});

// Store for managing blob URLs to prevent memory leaks
const blobUrlCache = new Map();

export const contentCacheService = {
  /**
   * Cache content from URL for offline access
   * @param {string} url - Content URL (Google Drive, direct URL, etc.)
   * @param {string} contentId - Unique content ID for cache key
   * @param {string} contentName - Content name for reference
   * @param {string} contentType - Type of content (book, course, exam, etc.)
   */
  async cacheContent(url, contentId, contentName, contentType = 'unknown') {
    try {
      // console.log(`Attempting to cache ${contentType}: ${contentName}`);
      
      // Try multiple approaches for Google Drive URLs
      const urls = this.getAlternativeUrls(url);
      let blob = null;
      let successUrl = null;

      for (const tryUrl of urls) {
        try {
          // console.log(`Trying URL: ${tryUrl}`);
          const response = await fetch(tryUrl, {
            mode: 'cors',
            credentials: 'omit'
          });
          
          if (response.ok) {
            blob = await response.blob();
            successUrl = tryUrl;
            // console.log(`Successfully fetched from: ${tryUrl}`);
            break;
          }
        } catch (fetchError) {
          // console.warn(`Failed to fetch from ${tryUrl}:`, fetchError.message);
          continue;
        }
      }

      if (!blob) {
        // If direct fetch fails, try using a proxy approach or store the URL for iframe fallback
        // console.warn(`Could not fetch content directly, storing URL for iframe fallback`);
        const cacheEntry = {
          blob: null,
          url: this.convertToPreviewUrl(url),
          originalUrl: url,
          contentName,
          contentType,
          timestamp: Date.now(),
          size: 0,
          fallbackMode: true
        };
        
        await contentCacheStore.setItem(`${contentType}_${contentId}`, cacheEntry);
        return true;
      }
      
      // Store the blob with metadata
      const cacheEntry = {
        blob,
        url: successUrl,
        originalUrl: url,
        contentName,
        contentType,
        timestamp: Date.now(),
        size: blob.size,
        fallbackMode: false
      };
      
      await contentCacheStore.setItem(`${contentType}_${contentId}`, cacheEntry);
      // console.log(`Content cached successfully for ${contentType}: ${contentName} (${blob.size} bytes)`);
      return true;
    } catch (error) {
      // console.error('Error caching content:', error);
      return false;
    }
  },

  /**
   * Get cached content blob URL for offline viewing
   * @param {string} contentId - Content ID
   * @param {string} contentType - Content type
   */
  async getCachedContent(contentId, contentType = 'unknown') {
    try {
      const cacheKey = `${contentType}_${contentId}`;
      const cacheEntry = await contentCacheStore.getItem(cacheKey);
      if (!cacheEntry) {
        return null;
      }

      // If it's fallback mode (no blob), return the preview URL
      if (cacheEntry.fallbackMode || !cacheEntry.blob) {
        return {
          blobUrl: cacheEntry.url,
          originalUrl: cacheEntry.originalUrl,
          contentName: cacheEntry.contentName,
          contentType: cacheEntry.contentType,
          timestamp: cacheEntry.timestamp,
          size: cacheEntry.size,
          isFallback: true
        };
      }

      // Check if we already have a blob URL for this content
      if (blobUrlCache.has(cacheKey)) {
        const existingBlobUrl = blobUrlCache.get(cacheKey);
        return {
          blobUrl: existingBlobUrl,
          originalUrl: cacheEntry.originalUrl,
          contentName: cacheEntry.contentName,
          contentType: cacheEntry.contentType,
          timestamp: cacheEntry.timestamp,
          size: cacheEntry.size,
          isFallback: false
        };
      }

      // Create new blob URL and cache it
      const blobUrl = URL.createObjectURL(cacheEntry.blob);
      blobUrlCache.set(cacheKey, blobUrl);
      
      return {
        blobUrl,
        originalUrl: cacheEntry.originalUrl,
        contentName: cacheEntry.contentName,
        contentType: cacheEntry.contentType,
        timestamp: cacheEntry.timestamp,
        size: cacheEntry.size,
        isFallback: false
      };
    } catch (error) {
      // console.error('Error retrieving cached content:', error);
      return null;
    }
  },

  /**
   * Get all cached items with metadata
   */
  async getCachedItems() {
    try {
      const keys = await contentCacheStore.keys();
      const items = [];
      
      for (const key of keys) {
        try {
          const cacheEntry = await contentCacheStore.getItem(key);
          if (cacheEntry) {
            const [contentType, contentId] = key.split('_');
            items.push({
              contentId,
              contentType,
              contentName: cacheEntry.contentName,
              size: cacheEntry.size || 0,
              timestamp: cacheEntry.timestamp,
              fallbackMode: cacheEntry.fallbackMode || false
            });
          }
        } catch (error) {
          // console.warn(`Error reading cache entry ${key}:`, error);
        }
      }
      
      return items.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    } catch (error) {
      // console.error('Error getting cached items:', error);
      return [];
    }
  },

  /**
   * Remove specific cached content
   * @param {string} contentId - Content ID
   * @param {string} contentType - Content type
   */
  async removeCachedContent(contentId, contentType) {
    try {
      const cacheKey = `${contentType}_${contentId}`;
      
      // Clean up blob URL if it exists
      if (blobUrlCache.has(cacheKey)) {
        URL.revokeObjectURL(blobUrlCache.get(cacheKey));
        blobUrlCache.delete(cacheKey);
      }
      
      await contentCacheStore.removeItem(cacheKey);
      // console.log(`Removed cached content: ${cacheKey}`);
      return true;
    } catch (error) {
      // console.error('Error removing cached content:', error);
      return false;
    }
  },

  /**
   * Check if content is cached
   * @param {string} contentId - Content ID
   * @param {string} contentType - Content type
   */
  async isContentCached(contentId, contentType = 'unknown') {
    try {
      const cacheEntry = await contentCacheStore.getItem(`${contentType}_${contentId}`);
      return !!cacheEntry;
    } catch (error) {
      // console.error('Error checking content cache:', error);
      return false;
    }
  },

  // Legacy methods for backward compatibility
  async cachePDF(url, bookId, bookName) {
    return this.cacheContent(url, bookId, bookName, 'book');
  },

  async getCachedPDF(bookId) {
    return this.getCachedContent(bookId, 'book');
  },

  async isPDFCached(bookId) {
    return this.isContentCached(bookId, 'book');
  },

  /**
   * Get alternative URLs to try for caching
   * @param {string} url - Original URL
   */
  getAlternativeUrls(url) {
    const urls = [];
    
    // Extract file ID from Google Drive URL
    const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      
      // Try different Google Drive URL formats
      urls.push(`https://drive.google.com/uc?export=download&id=${fileId}`);
      urls.push(`https://drive.google.com/uc?id=${fileId}&export=download`);
      urls.push(`https://docs.google.com/document/d/${fileId}/export?format=pdf`);
      urls.push(`https://drive.google.com/file/d/${fileId}/view`);
    }
    
    // Add original URL as fallback
    urls.push(url);
    
    return urls;
  },

  /**
   * Convert Google Drive view URL to direct download URL
   * @param {string} url - Google Drive view URL
   */
  convertToDirectUrl(url) {
    // Extract file ID from Google Drive URL
    const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }
    
    // If it's already a direct URL or different format, return as is
    return url;
  },

  /**
   * Convert Google Drive view URL to preview URL for iframe
   * @param {string} url - Google Drive view URL
   */
  convertToPreviewUrl(url) {
    return url.replace('/view', '/preview');
  },

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    try {
      const keys = await contentCacheStore.keys();
      let totalSize = 0;
      const cachedContent = [];

      for (const key of keys) {
        const entry = await contentCacheStore.getItem(key);
        if (entry) {
          totalSize += entry.size || 0;
          const [contentType, contentId] = key.split('_', 2);
          cachedContent.push({
            contentId,
            contentType,
            contentName: entry.contentName,
            size: entry.size,
            timestamp: entry.timestamp,
          });
        }
      }

      return {
        totalItems: keys.length,
        totalSize,
        cachedContent,
      };
    } catch (error) {
      // console.error('Error getting cache stats:', error);
      return { totalItems: 0, totalSize: 0, cachedContent: [] };
    }
  },

  /**
   * Clear all cached content
   */
  async clearCache() {
    try {
      // Revoke all blob URLs
      for (const blobUrl of blobUrlCache.values()) {
        URL.revokeObjectURL(blobUrl);
      }
      blobUrlCache.clear();
      
      await contentCacheStore.clear();
      return true;
    } catch (error) {
      // console.error('Error clearing content cache:', error);
      return false;
    }
  },

  /**
   * Cleanup blob URLs (call this when component unmounts)
   */
  cleanupBlobUrls() {
    for (const [key, blobUrl] of blobUrlCache.entries()) {
      URL.revokeObjectURL(blobUrl);
    }
    blobUrlCache.clear();
  },

  /**
   * Force refresh cached content
   * @param {string} contentId - Content ID
   * @param {string} contentType - Content type
   */
  async refreshCachedContent(contentId, contentType = 'unknown') {
    try {
      const cacheKey = `${contentType}_${contentId}`;
      const cacheEntry = await contentCacheStore.getItem(cacheKey);
      
      if (!cacheEntry) {
        return false;
      }

      // Remove old cache
      await this.removeCachedContent(contentId, contentType);
      
      // Re-cache with original URL
      return await this.cacheContent(
        cacheEntry.originalUrl, 
        contentId, 
        cacheEntry.contentName, 
        contentType
      );
    } catch (error) {
      // console.error('Error refreshing cached content:', error);
      return false;
    }
  },
};

// Export both names for compatibility
export const pdfCacheService = contentCacheService;
export default contentCacheService;